/*=========================================================================================
  File Name: moduleDeviceGetters.js
  Description: Device Module Getters
==========================================================================================*/
import _ from "lodash";
import constant from "../../utils/constant";

const formatTreeData = arr => {
	var tree = [],
		mappedArr = {},
		arrElem,
		mappedElem;
	// First map the nodes of the array to an object -> create a hash table.
	for (var i = 0, len = arr.length; i < len; i++) {
		arrElem = {
			...arr[i],
			data: { ...arr[i] },
			text: arr[i].name,
			state: {
				expanded: true
			}
		};
		mappedArr[arrElem.id] = arrElem;
		mappedArr[arrElem.id]["children"] = [];
	}

	for (var id in mappedArr) {
		if (mappedArr.hasOwnProperty(id)) {
			mappedElem = mappedArr[id];
			// If the element is not at the root level, add it to its parent array of children.
			if (mappedElem.parent_id && mappedArr[mappedElem["parent_id"]]) {
				mappedArr[mappedElem["parent_id"]]["children"].push(mappedElem);
			}
			// If the element is at the root level, add it to first level elements array.
			else {
				tree.push(mappedElem);
			}
		}
	}
	return tree;
};

const flattenTreeData = (node, result = [], depth = 1) => {
	node.forEach(child => {
		let flattened = Object.assign({}, child);
		delete flattened.children;
		flattened.depth = depth;
		result.push(flattened);
		if (child.children && child.children.length) {
			flattenTreeData(child.children, result, depth + 1);
		}
	});
	return result;
};

const formatTaskList = device => {
	const taskList =
		!_.isEmpty(device.incident) && _.get(device, "incident.task.id") === _.get(device, "tasks.data.0.id")
			? [{ ...device.incident, isIncident: true }, ...(_.get(device, "tasks.data.0.details") || [])]
			: _.get(device, "tasks.data.0.details") || [];

	return taskList.map(task => ({
		...task,
		updatedTime: task.updated_at
	}));
};

const formatSensorData = (properties, accessory) => {
	const currentValue = _.get(accessory, "sensors.0.current") || 0;
	const limit = _.get(properties, "sensor_threshold");
	let unit = _.get(properties, "sensor_unit") || "";

	if (!properties.sensor_type || properties.sensor_type === "none") {
		return null;
	}
	if (properties.sensor_type === "temperature") {
		unit = "Celsius";
	}

	if (properties.sensor_type === "electric") {
		unit = "Hour";
	}
	unit = unit.charAt(0).toUpperCase() + unit.slice(1);
	return {
		isActive: _.get(accessory, "sensors.0.is_active") || false,
		isOverLimit: limit === null ? false : currentValue > limit,
		value: currentValue,
		limit,
		unit
	};
};

export const getAccessory = (accessory, state) => {
	if (accessory.type_id === constant.ACCESSORY_TYPE.ACCESSORY) {
		return state.accessories.find(itemAccessory => itemAccessory.id === accessory.id);
	} else if (accessory.type_id === constant.ACCESSORY_TYPE.MATERIAL) {
		return state.material.find(itemMaterial => itemMaterial.id === accessory.id);
	}
};

export const parsedDevice = (device, state) => {
	return {
		...device,
		categoryName: _.get(device, "category.name") || "",
		imageSource: _.get(device, "image.path") || "",
		employeeName: _.get(device, "tasks.data.0.employee.name") || "",
		inventoryName: _.get(device, "inventories.data.0.name") || "",
		managerName: (_.get(device, "manage_employees.data") || []).map(user => user.display_name).join(", "),
		statusName: _.get(_.find(state.devicesStatus, ["id", device.status]) || {}, "name") || "",
		statusColor: _.get(_.find(state.devicesStatus, ["id", device.status]) || {}, "color") || "",
		isHasChecklist: function() {
			const deviceCategory =
				_.find(state.categories, {
					id: _.get(device, "category.id")
				}) || {};
			if (deviceCategory) {
				return !!(_.get(deviceCategory, "kpis.0.kpi_details") || []).length;
			}
			return false;
		},
		checklist: _.get(device, "kpis.0.kpi_details") || [],
		tasklist: formatTaskList(device)
	};
};

export default {
	queriedDevices: state =>
		state.devices.filter(device => {
			let isItemOfCurrentFilter = false;

			if ((state.devicesFilter === "all" && !device.isTrashed) || (state.devicesFilter === "trashed" && device.isTrashed)) {
				isItemOfCurrentFilter = true;
			}

			return isItemOfCurrentFilter && (device.title.toLowerCase().includes(state.devicesSearchQuery.toLowerCase()) || device.desc.toLowerCase().includes(state.devicesSearchQuery.toLowerCase()));
		}),
	getDevice: state => deviceId => state.devices.find(device => device.id === deviceId),
	getParsedDevice: state => device => {
		return parsedDevice(device, state);
	},
	getParsedDevices: state => state.devices.map(device => parsedDevice(device, state)),
	getParsedCategories: state =>
		state.categories.map(category => ({
			...category,
			isInUse: _.some(state.categories, tempCategory => !!_.find(state.devices, device => category.id === tempCategory.id && _.get(device, "category.id") === tempCategory.id))
		})),
	getTreeCategories: (state, getters) => formatTreeData(getters.getParsedCategories),
	getFormattedFlattenTreeCategories: (state, getters) => flattenTreeData(getters.getTreeCategories),
	getParsedAccessories: state =>
		state.accessories.map(accessory => ({
			...accessory,
			imageSource: _.get(accessory, "image.path") || "",
			readableExtraProperty: accessory.properties ? JSON.parse(accessory.properties).extra : "",
			readableProperties: accessory.properties ? JSON.parse(accessory.properties) : "",
			sensorData: accessory.properties ? formatSensorData(JSON.parse(accessory.properties), accessory) : null
		})),
	getParsedMaterial: state =>
		state.material.map(material => ({
			...material,
			imageSource: _.get(material, "image.path") || "",
			readableExtraProperty: material.properties ? JSON.parse(material.properties).extra : "",
			readableProperties: material.properties ? JSON.parse(material.properties) : "",
			sensorData: material.properties ? formatSensorData(JSON.parse(material.properties), material) : null
		})),
	getParsedAccessory: state => accessory => {
		return getAccessory(accessory, state);
	},
	getParsedRemindWork: state =>
		state.remindWork.map(material => ({
			...material,
			imageSource: _.get(material, "image.path") || "",
			readableExtraProperty: material.properties ? JSON.parse(material.properties).extra : "",
			readableProperties: material.properties ? JSON.parse(material.properties) : "",
			sensorData: material.properties ? formatSensorData(JSON.parse(material.properties), material) : null
		}))
};
